import { Component, EventEmitter, Input, OnDestroy, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * A single selectable status option within a group.
 * `token` is the composite "bundle:tid" value the backend expects.
 */
interface StatusOption {
  token: string;
  tid: string;
  name: string;
}

/** A named group of status options (Appointment / Group lesson / Service). */
interface StatusGroup {
  /** The events bundle this group maps to (lesson / group_lesson / services). */
  bundle: string;
  label: string;
  options: StatusOption[];
}

/**
 * Grouped, searchable, multi-select status filter for the Appointment List.
 *
 * Statuses are grouped by event type. Appointment (lesson) and Group lesson
 * both draw from the lesson_status vocabulary; Service draws from
 * service_status. Because the same status term can appear under more than one
 * group, each selection is encoded as a composite "bundle:tid" token
 * (e.g. "lesson:59", "group_lesson:59", "services:106") so the group is never
 * ambiguous. The bound value is an array of those tokens.
 *
 * Implemented as a ControlValueAccessor so it drops into the existing ngForm
 * (name="field_status_target_id[]") exactly like the native mat-select it
 * replaces; the parent's onSubmit() serialises the token array into the query.
 */
@Component({
  selector: 'app-status-group-filter',
  templateUrl: './status-group-filter.component.html',
  styleUrls: ['./status-group-filter.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StatusGroupFilterComponent),
      multi: true,
    },
  ],
})
export class StatusGroupFilterComponent implements ControlValueAccessor, OnDestroy {
  /** lesson_status taxonomy terms ({ id, name }); shared by Appointment + Group lesson. */
  @Input() set lessonStatuses(value: { id: string; name: string }[]) {
    this._lessonStatuses = value || [];
    this.buildGroups();
  }

  /** service_status taxonomy terms ({ id, name }); used by the Service group. */
  @Input() set serviceStatuses(value: { id: string; name: string }[]) {
    this._serviceStatuses = value || [];
    this.buildGroups();
  }

  /**
   * Per-group facet counts from the list response, keyed by bundle then tid:
   * { lesson: { '59': 541 }, group_lesson: {...}, services: { '106': 34 } }.
   */
  @Input() set counts(value: { [bundle: string]: { [tid: string]: number } }) {
    this._counts = value || {};
  }
  get counts() {
    return this._counts;
  }

  /** Fires after the dropdown closes with a changed selection. */
  @Output() selectionChanged = new EventEmitter<string[]>();

  groups: StatusGroup[] = [];
  selected: string[] = [];
  searchText = '';

  private _lessonStatuses: { id: string; name: string }[] = [];
  private _serviceStatuses: { id: string; name: string }[] = [];
  private _counts: { [bundle: string]: { [tid: string]: number } } = {};

  // Toggling checkboxes fires a rapid burst of changes; debounce so several
  // quick picks coalesce into a single request shortly after you stop, instead
  // of one request per checkbox (or waiting until you click out of the panel).
  private readonly _emit$ = new Subject<void>();
  private readonly _emitSub: Subscription;
  // The selection last actually emitted, so we never refire an unchanged value.
  private _lastEmitted = '';

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this._emitSub = this._emit$
      .pipe(debounceTime(450))
      .subscribe(() => this.flushSelection());
  }

  ngOnDestroy(): void {
    this._emitSub?.unsubscribe();
  }

  // --- ControlValueAccessor ------------------------------------------------

  writeValue(value: string[] | string | null): void {
    if (Array.isArray(value)) {
      this.selected = [...value];
    }
    else if (typeof value === 'string' && value !== '') {
      // A shared link round-trips the tokens as a comma-joined string.
      this.selected = value.split(',').map(v => v.trim()).filter(v => v !== '');
    }
    else {
      this.selected = [];
    }
    // Keep the "last emitted" baseline in sync with programmatic writes (URL
    // restore, form reset) so they don't get mistaken for a user change and
    // trigger a redundant request.
    this._lastEmitted = this.selected.slice().sort().join(',');
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // --- View helpers --------------------------------------------------------

  private buildGroups(): void {
    const lessonOptions = (bundle: string): StatusOption[] =>
      this._lessonStatuses.map(s => ({ token: `${bundle}:${s.id}`, tid: s.id, name: s.name }));

    this.groups = [
      { bundle: 'lesson', label: 'Appointment', options: lessonOptions('lesson') },
      { bundle: 'group_lesson', label: 'Group lesson', options: lessonOptions('group_lesson') },
      {
        bundle: 'services',
        label: 'Service',
        options: this._serviceStatuses.map(s => ({ token: `services:${s.id}`, tid: s.id, name: s.name })),
      },
    ];
  }

  /** Options for a group, filtered by the search box. */
  visibleOptions(group: StatusGroup): StatusOption[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return group.options;
    }
    return group.options.filter(o => o.name.toLowerCase().includes(term));
  }

  /** Whether a group has any option matching the current search. */
  groupHasVisibleOptions(group: StatusGroup): boolean {
    return this.visibleOptions(group).length > 0;
  }

  /** The facet count for an option, or null when unknown (no date range set). */
  optionCount(group: StatusGroup, option: StatusOption): number | null {
    const groupCounts = this._counts[group.bundle];
    if (!groupCounts || groupCounts[option.tid] === undefined) {
      return null;
    }
    return groupCounts[option.tid];
  }

  /** Compact chip label for the closed select's trigger, e.g. "Showed", "Sale". */
  triggerLabels(): string[] {
    const byToken = new Map<string, string>();
    this.groups.forEach(g => g.options.forEach(o => byToken.set(o.token, o.name)));
    return this.selected.map(token => byToken.get(token) ?? token);
  }

  // --- Events --------------------------------------------------------------

  onSelectionModelChange(value: string[]): void {
    this.selected = value;
    this.onChange(this.selected);
    // Debounced: fires the report shortly after the last toggle without
    // waiting for the panel to close.
    this._emit$.next();
  }

  onOpenedChange(opened: boolean): void {
    if (opened) {
      this.searchText = '';
    }
    else {
      this.onTouched();
      // Closing the panel flushes any pending change immediately, so clicking
      // out never leaves a picked-but-not-applied selection.
      this.flushSelection();
    }
  }

  clearSelection(): void {
    this.selected = [];
    this.onChange(this.selected);
    this.flushSelection();
  }

  /** Emit the current selection now if it differs from the last emitted one. */
  private flushSelection(): void {
    const now = this.selected.slice().sort().join(',');
    if (now !== this._lastEmitted) {
      this._lastEmitted = now;
      this.selectionChanged.emit(this.selected);
    }
  }

  /** Prevent typing in the search box from being swallowed as select typeahead. */
  onSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }
}
